import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const open = index !== null;

  const next = useCallback(
    () => onNavigate((index + 1) % items.length),
    [index, items.length, onNavigate]
  );
  const prev = useCallback(
    () => onNavigate((index - 1 + items.length) % items.length),
    [index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, next, prev]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-forest-deep/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close-button"
            aria-label="Close image viewer"
            onClick={onClose}
            className="absolute top-5 right-5 p-3 rounded-full text-ivory/80 hover:text-gold hover:bg-ivory/10 transition-colors duration-300 z-10"
          >
            <X size={24} />
          </button>
          <button
            data-testid="lightbox-prev-button"
            aria-label="Previous image"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 md:left-8 p-3 rounded-full text-ivory/80 hover:text-gold hover:bg-ivory/10 transition-colors duration-300 z-10"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            data-testid="lightbox-next-button"
            aria-label="Next image"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 md:right-8 p-3 rounded-full text-ivory/80 hover:text-gold hover:bg-ivory/10 transition-colors duration-300 z-10"
          >
            <ChevronRight size={28} />
          </button>
          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[index].src}
              alt={items[index].alt}
              className="w-full max-h-[78vh] object-contain rounded-xl"
            />
            <figcaption className="mt-4 text-center text-ivory/60 text-sm">
              {items[index].alt}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
